'use client';

import { isIntranetHost, isIntranetPath } from '@/lib/intranet';
import React, { useEffect, useMemo, useState } from 'react';

const clientResolutionCache = new Map<string, string>();

type InternalLinkAnchorProps = {
  internalUrl: string;
  className?: string;
  children: React.ReactNode;
  isPreview?: boolean;
};

const FALLBACK_HREF = '/page-not-found';

const getClientIsIntranet = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    isIntranetHost(window.location.host) ||
    isIntranetPath(window.location.pathname)
  );
};

const InternalLinkAnchor = ({
  internalUrl,
  className,
  children,
  isPreview = false,
}: InternalLinkAnchorProps) => {
  const isIntranet = useMemo(() => getClientIsIntranet(), []);
  const cacheKey = useMemo(
    () =>
      `${isIntranet ? 'intranet' : 'public'}:${isPreview ? 'preview' : 'live'}:${internalUrl}`,
    [internalUrl, isPreview, isIntranet],
  );
  const [href, setHref] = useState<string>(
    clientResolutionCache.get(cacheKey) || FALLBACK_HREF,
  );

  useEffect(() => {
    let cancelled = false;
    const cached = clientResolutionCache.get(cacheKey);
    if (cached) {
      setHref(cached);
      return;
    }

    const params = new URLSearchParams({
      internalUrl,
      isPreview: String(isPreview),
      isIntranet: String(isIntranet),
    });

    fetch(`/api/internal-link/resolve?${params.toString()}`)
      .then((res) => res.json())
      .then((payload: { url?: string }) => {
        if (cancelled) return;
        const resolved = payload?.url || FALLBACK_HREF;
        clientResolutionCache.set(cacheKey, resolved);
        setHref(resolved);
      })
      .catch(() => {
        if (cancelled) return;
        clientResolutionCache.set(cacheKey, FALLBACK_HREF);
        setHref(FALLBACK_HREF);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, internalUrl, isPreview, isIntranet]);

  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
};

export default InternalLinkAnchor;
